import { flexRender, type RowData } from "@tanstack/react-table";
import { useMemo } from "react";
import { Link, useLocation } from "react-router";

import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { HistogramChart } from "@/components/Charts/HistogramChart";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { FormattedDuration } from "@/components/FormattedDuration/FormattedDuration";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Pagination } from "@/components/Pagination/Pagination";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { SectionCard } from "@/design-system/SectionCard";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import type { DashboardTable } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { type TabItem, Tabs } from "@/design-system/Tabs";
import { formatSignedChange } from "@/helpers/util";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsScope } from "@/modules/analytics/AnalyticsScopeContext";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { AnalyticsPageContent } from "@/modules/analytics/components/AnalyticsPageContent";
import { AnalyticsSection } from "@/modules/analytics/components/AnalyticsSection";
import { BarChart } from "@/modules/analytics/components/BarChart";
import { LineChart } from "@/modules/analytics/components/LineChart";
import { ANALYTICS_AUDIENCE_SCOPE_COLORS, ANALYTICS_TABLE_PAGE_SIZE } from "@/modules/analytics/constants";
import type {
  BookingAnalyticsAssetRow,
  BookingAnalyticsBookerType,
  BookingAnalyticsBookingRow,
  BookingAnalyticsDurationBucket,
  BookingAnalyticsResponse,
  BookingAnalyticsTimeOfDay,
  BookingAnalyticsWeekday,
} from "@/queries/analytics/booking-reporting-types";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

import { hasProjectConnectionAudience } from "./audience-scopes";
import type { BookingAnalyticsChartFilter, BookingAnalyticsDateRangeOption } from "./BookingAnalyticsPage";

const ASSET_CHART_SIZE = 8;

export type BookingAnalyticsTableTab = "bookings" | "assets";

interface Props {
  mode?: "focused" | "all";
  data: BookingAnalyticsResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  selectedDateRange: BookingAnalyticsDateRangeOption;
  dateRangeOptions: NonEmptyArray<BookingAnalyticsDateRangeOption>;
  onDateRangeChange: (option: BookingAnalyticsDateRangeOption) => void;
  selectedTableTab: BookingAnalyticsTableTab;
  onTableTabChange: (tab: BookingAnalyticsTableTab) => void;
  search: string;
  onSearchChange: (search: string) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
  bookingTable: DashboardTable<BookingAnalyticsBookingRow>;
  assetTable: DashboardTable<BookingAnalyticsAssetRow>;
  chartFilter: BookingAnalyticsChartFilter;
  onChartFilter: (filter: NonNullable<BookingAnalyticsChartFilter>) => void;
  onAssetFilter: (assetId: string) => void;
  onClearChartFilter: () => void;
  page: number;
  pageCount: number;
  bookingResultCount: number;
  assetResultCount: number;
  onPageChange: (page: number) => void;
}

export function BookingAnalyticsContent({
  mode = "all",
  data,
  isLoading,
  isFetching,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
  selectedTableTab,
  onTableTabChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
  bookingTable,
  assetTable,
  chartFilter,
  onChartFilter,
  onAssetFilter,
  onClearChartFilter,
  page,
  pageCount,
  bookingResultCount,
  assetResultCount,
  onPageChange,
}: Props): React.ReactNode {
  const { t } = useTranslation();
  const analyticsScope = useAnalyticsScope();
  const location = useLocation();
  const slug = useSlug({ optional: true });
  const projectId = useProjectId();
  const portfolioRoute = (path: string) => addPortfolioProjectFilterQueryParams(path, location.search);
  const combinedFilterCount =
    activeFilterCount + (analyticsScope.type === "portfolio" ? analyticsScope.projectFilter.activeCount : 0);

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={mode === "focused" ? t("page.analytics-bookings.focus.title") : t("page.analytics-bookings.title")}
      title={mode === "focused" ? t("page.analytics-bookings.focus.title") : t("page.analytics-bookings.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            analyticsScope.type === "portfolio"
              ? { name: t("page.portfolio.title"), to: portfolioRoute(routes.portfolio.overview()) }
              : { name: t("page.analytics-story.title"), to: routes.analytics.overview({ slug: slug! }) },
            { name: t("page.analytics-bookings.breadcrumb") },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: analyticsScope.type === "portfolio" ? analyticsScope.projectIds : [projectId] }}
          context={{ scope: analyticsScope.type, entity: "analytics", page: "detail" }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics.suggestion.2"),
          ]}
        />
      }
      header={
        <Inline gap="md" align="center">
          <Inline gap="sm" align="center" wrap={false}>
            <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
            <Select
              items={dateRangeOptions}
              selected={selectedDateRange}
              onChange={onDateRangeChange}
              renderOption={(option) => option.name}
              keySelector={(option) => option.value}
            />
          </Inline>
          <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
            <Inline as="span" gap="sm" align="center" wrap={false}>
              {t("common.action.filter")}
              {combinedFilterCount > 0 && <NumericBadge count={combinedFilterCount} variant="filled" />}
            </Inline>
          </Button>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </Inline>
      }
    >
      <AnalyticsPageContent>
        {mode === "focused" && (
          <p className="max-w-measure-wide text-body text-text-secondary">
            {t("page.analytics-bookings.focus.description")}
          </p>
        )}
        {isLoading || !data ? (
          <section className="flex flex-1 items-center justify-center py-space-xl" aria-busy="true">
            <Spinner size="xl" />
          </section>
        ) : (
          <Content
            mode={mode}
            data={data}
            selectedTableTab={selectedTableTab}
            onTableTabChange={onTableTabChange}
            search={search}
            onSearchChange={onSearchChange}
            activeFilterCount={combinedFilterCount}
            onOpenFilters={onOpenFilters}
            bookingTable={bookingTable}
            assetTable={assetTable}
            chartFilter={chartFilter}
            onChartFilter={onChartFilter}
            onAssetFilter={onAssetFilter}
            onClearChartFilter={onClearChartFilter}
            page={page}
            pageCount={pageCount}
            bookingResultCount={bookingResultCount}
            assetResultCount={assetResultCount}
            onPageChange={onPageChange}
          />
        )}
      </AnalyticsPageContent>
    </DocumentPage>
  );
}

function Content({
  mode,
  data,
  selectedTableTab,
  onTableTabChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
  bookingTable,
  assetTable,
  chartFilter,
  onChartFilter,
  onAssetFilter,
  onClearChartFilter,
  page,
  pageCount,
  bookingResultCount,
  assetResultCount,
  onPageChange,
}: Pick<
  Props,
  | "mode"
  | "selectedTableTab"
  | "onTableTabChange"
  | "search"
  | "onSearchChange"
  | "activeFilterCount"
  | "onOpenFilters"
  | "bookingTable"
  | "assetTable"
  | "chartFilter"
  | "onChartFilter"
  | "onAssetFilter"
  | "onClearChartFilter"
  | "page"
  | "pageCount"
  | "bookingResultCount"
  | "assetResultCount"
  | "onPageChange"
> & {
  data: BookingAnalyticsResponse;
}): React.ReactNode {
  const { i18n, t } = useTranslation();
  const help = useMetricHelp();
  const analyticsScope = useAnalyticsScope();
  const isPortfolio = analyticsScope.type === "portfolio";
  const slug = useSlug({ optional: true });
  const { summary } = data;

  const bookingsData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.bookings,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const cancelledData = useMemo(
    () =>
      data.monthly.map((point) => ({
        x: point.x,
        y: point.cancelled,
        isPending: point.isCurrentMonth,
      })),
    [data.monthly],
  );
  const uniqueBookerData = useMemo(
    () => [
      {
        label: t("page.analytics-bookings.overview.unique-bookers"),
        color: dataColors.green,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.uniqueBookers,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const utilizationData = useMemo(
    () => [
      {
        label: t("page.analytics-bookings.utilization.chart-title"),
        color: dataColors.orange,
        dataPoints: data.monthly.map((point) => ({
          x: point.x,
          y: point.utilizationPercentage,
          isPending: point.isCurrentMonth,
        })),
      },
    ],
    [data.monthly, t],
  );
  const audienceData = useMemo(
    () =>
      data.audienceScopes.map((scope, index) => ({
        label:
          scope.type === "projectOnly"
            ? t("page.analytics-bookings.origin.project-only")
            : (scope.projectConnection?.name ?? t("page.analytics-bookings.filters.origin.project-connection")),
        color: ANALYTICS_AUDIENCE_SCOPE_COLORS[index % ANALYTICS_AUDIENCE_SCOPE_COLORS.length],
        dataPoints: scope.dataPoints.map((point) => ({
          x: point.x,
          y: point.bookings,
          isPending: point.isCurrentMonth,
        })),
      })),
    [data.audienceScopes, t],
  );
  const assetUtilizationData = useMemo(
    () =>
      [...data.assets]
        .sort((a, b) => b.utilizationPercentage - a.utilizationPercentage)
        .slice(0, ASSET_CHART_SIZE)
        .map((asset) => ({ id: asset.id, x: asset.name, y: asset.utilizationPercentage })),
    [data.assets],
  );
  const durationData = useMemo(
    () =>
      data.durationDistribution.map((point) => ({
        category: point.x as BookingAnalyticsDurationBucket,
        label: t(`page.analytics-bookings.duration.${point.x as BookingAnalyticsDurationBucket}`),
        value: point.y,
      })),
    [data.durationDistribution, t],
  );
  const weekdayData = useMemo(
    () =>
      data.weekdayDistribution.map((point) => ({
        category: point.x as BookingAnalyticsWeekday,
        label: t(`common.date.weekday.${point.x as BookingAnalyticsWeekday}`),
        value: point.y,
      })),
    [data.weekdayDistribution, t],
  );
  const timeOfDayData = useMemo(
    () =>
      data.timeOfDayDistribution.map((point) => ({
        category: point.x as BookingAnalyticsTimeOfDay,
        label: t(`page.analytics-bookings.time-of-day.${point.x as BookingAnalyticsTimeOfDay}`),
        value: point.y,
      })),
    [data.timeOfDayDistribution, t],
  );
  const bookerData = useMemo(
    () =>
      data.bookerDistribution.map((point) => ({
        category: point.x as BookingAnalyticsBookerType,
        label: t(`page.analytics-bookings.booker-type.${point.x as BookingAnalyticsBookerType}`),
        value: point.y,
      })),
    [data.bookerDistribution, t],
  );
  const utilizationMaxY = useMemo(() => {
    const highestUtilization = Math.max(
      ...data.monthly.map((point) => point.utilizationPercentage),
      ...data.assets.map((asset) => asset.utilizationPercentage),
    );

    return Math.min(100, Math.max(20, Math.ceil(highestUtilization / 10) * 10));
  }, [data.assets, data.monthly]);
  const tableTabs = useMemo<TabItem<BookingAnalyticsTableTab>[]>(
    () => [
      {
        label: t("page.analytics-bookings.tabs.bookings"),
        value: "bookings",
        count: bookingResultCount,
        showZeroCount: true,
      },
      {
        label: t("page.analytics-bookings.tabs.assets"),
        value: "assets",
        count: assetResultCount,
        showZeroCount: true,
      },
    ],
    [assetResultCount, bookingResultCount, t],
  );

  const hasProjectConnections = hasProjectConnectionAudience(data.audienceScopes);
  const resultCount = selectedTableTab === "bookings" ? bookingResultCount : assetResultCount;
  const rangeFrom = resultCount === 0 ? 0 : page * ANALYTICS_TABLE_PAGE_SIZE + 1;
  const rangeTo = Math.min((page + 1) * ANALYTICS_TABLE_PAGE_SIZE, resultCount);

  return (
    <PageGrid.Row>
      <PageGrid.Column size="100%">
        <AnalyticsSection
          isFullWidth
          title={
            mode === "focused"
              ? t("page.analytics-bookings.focus.signals")
              : t("page.analytics-bookings.overview.section-title")
          }
        >
          <AnalyticsCard.Root>
            <AnalyticsCard.Header
              title={t("page.analytics-bookings.overview.title")}
              tooltip={help("page.analytics-bookings.overview.title.tooltip")}
            />
            <section className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
              <Stat
                label={t("page.analytics-bookings.overview.active-assets")}
                value={summary.activeAssets}
                renderFooter={
                  analyticsScope.type === "project"
                    ? () => t("page.analytics-bookings.overview.active-assets-total", { total: data.assets.length })
                    : undefined
                }
              />
              <Stat
                label={t("page.analytics-bookings.overview.bookings")}
                value={summary.bookings}
                renderFooter={() =>
                  t("page.analytics-bookings.overview.previous-period-change", {
                    change: formatSignedChange(summary.bookings - summary.previousPeriod.bookings, i18n.language),
                  })
                }
              />
              <Stat
                label={t("page.analytics-bookings.overview.unique-bookers")}
                value={summary.uniqueBookers}
                renderFooter={() =>
                  t("page.analytics-bookings.overview.resident-reach", {
                    percentage: summary.bookerReachPercentage,
                    total: summary.eligibleResidents,
                    change: formatSignedChange(
                      summary.uniqueBookers - summary.previousPeriod.uniqueBookers,
                      i18n.language,
                    ),
                  })
                }
              />
              <Stat
                label={t("page.analytics-bookings.overview.booked-hours")}
                value={summary.bookedHours}
                renderValue={(value) =>
                  value === undefined ? (
                    "-"
                  ) : (
                    <span className="text-heading-2 whitespace-nowrap">
                      <FormattedDuration duration={{ hours: value }} formatTokens={["hours", "minutes"]} />
                    </span>
                  )
                }
                renderFooter={() =>
                  t("page.analytics-bookings.overview.previous-period-change", {
                    change: formatSignedChange(summary.bookedHours - summary.previousPeriod.bookedHours, i18n.language),
                  })
                }
              />
              <Stat
                label={t("page.analytics-bookings.overview.utilization")}
                value={summary.utilizationPercentage}
                renderValue={(value) =>
                  value === undefined ? (
                    "-"
                  ) : (
                    <span className="whitespace-nowrap">
                      {value}
                      <span className="text-heading-2">%</span>
                    </span>
                  )
                }
                renderFooter={() =>
                  t("page.analytics-bookings.overview.percentage-point-change", {
                    change: formatSignedChange(
                      summary.utilizationPercentage - summary.previousPeriod.utilizationPercentage,
                      i18n.language,
                    ),
                  })
                }
              />
              <Stat
                label={t("page.analytics-bookings.overview.cancellation-rate")}
                value={summary.cancellationPercentage}
                renderValue={(value) =>
                  value === undefined ? (
                    "-"
                  ) : (
                    <span className="whitespace-nowrap">
                      {value}
                      <span className="text-heading-2">%</span>
                    </span>
                  )
                }
                renderFooter={() =>
                  t("page.analytics-bookings.overview.cancelled-change", {
                    count: summary.cancelledBookings,
                    change: formatSignedChange(
                      summary.cancellationPercentage - summary.previousPeriod.cancellationPercentage,
                      i18n.language,
                    ),
                  })
                }
              />
            </section>
          </AnalyticsCard.Root>
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && (
        <PageGrid.Column size="100%">
          <AnalyticsSection title={t("page.analytics-bookings.activity.section-title")}>
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-bookings.activity.bookings-cancelled")}
                tooltip={help("page.analytics-bookings.activity.bookings-cancelled.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <BarChart
                  dataPrimary={{
                    data: bookingsData,
                    onClick: (point) =>
                      onChartFilter({
                        type: "month",
                        value: String(point.x),
                        label: formatDate(i18n, "monthYear", point.x),
                      }),
                  }}
                  dataSecondary={{
                    data: cancelledData,
                    color: dataColors.orange,
                    onClick: (point) =>
                      onChartFilter({
                        type: "month",
                        value: String(point.x),
                        label: formatDate(i18n, "monthYear", point.x),
                      }),
                  }}
                  xAxisType="date"
                  yLabel={t("page.analytics-bookings.axis.bookings")}
                  yTickMode="integer"
                  hoverLabel={(bar) => (
                    <Stack gap="xs">
                      <span>
                        {t("page.analytics-bookings.overview.bookings")}: {bar.primary?.y ?? 0}
                      </span>
                      <span>
                        {t("page.analytics-bookings.overview.cancelled")}: {bar.secondary?.y ?? 0}
                      </span>
                      {(bar.primary?.isPending || bar.secondary?.isPending) && (
                        <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                      )}
                    </Stack>
                  )}
                />
                <ChartLegend
                  items={[
                    { label: t("page.analytics-bookings.overview.bookings"), color: dataColors.primary },
                    { label: t("page.analytics-bookings.overview.cancelled"), color: dataColors.orange },
                    {
                      label: t("page.analytics-details.engagement.chart.legend.current-period"),
                      color: dataColors.primary,
                      pattern: "striped",
                    },
                  ]}
                />
              </figure>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-bookings.activity.unique-bookers")}
                tooltip={help("page.analytics-bookings.activity.unique-bookers.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <LineChart
                  data={uniqueBookerData}
                  xAxisType="date"
                  yLabel={t("page.analytics-bookings.axis.residents")}
                  hoverLabel={(point) => (
                    <span>
                      {point.relatedPoints[0].dataPoint.y}
                      {point.relatedPoints[0].dataPoint.isPending
                        ? ` · ${t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}`
                        : ""}
                    </span>
                  )}
                />
                <ChartLegend items={[{ label: uniqueBookerData[0].label, color: dataColors.green }]} />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-bookings.utilization.section-title")}
          description={t("page.analytics-bookings.utilization.description")}
        >
          <AnalyticsCard.Root variant="graph" height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-bookings.utilization.chart-title")}
              tooltip={help("page.analytics-bookings.utilization.chart-title.tooltip")}
            />
            <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
              <LineChart
                data={utilizationData}
                xAxisType="date"
                yLabel={t("page.analytics-bookings.axis.percentage")}
                maxY={utilizationMaxY}
                formatYTick={(value) => `${value}%`}
                hoverLabel={(point) => (
                  <span>
                    {point.relatedPoints[0].dataPoint.y}%
                    {point.relatedPoints[0].dataPoint.isPending
                      ? ` · ${t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}`
                      : ""}
                  </span>
                )}
              />
              <ChartLegend items={[{ label: utilizationData[0].label, color: dataColors.orange }]} />
            </figure>
          </AnalyticsCard.Root>
          {!isPortfolio && (
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-bookings.utilization.by-asset")}
                tooltip={help("page.analytics-bookings.utilization.by-asset.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <BarChart
                  dataPrimary={{
                    data: assetUtilizationData,
                    color: dataColors.orange,
                    onClick: (point) => onAssetFilter(point.id),
                  }}
                  maxY={utilizationMaxY}
                  yLabel={t("page.analytics-bookings.axis.percentage")}
                  formatXTick={formatAssetLabel}
                  formatYTick={(value) => `${value}%`}
                  hoverLabel={(bar) => (
                    <span>
                      {bar.primary?.x}: {bar.primary?.y ?? 0}%
                    </span>
                  )}
                />
                <ChartLegend
                  items={[
                    {
                      label: t("page.analytics-bookings.utilization.available-hours-used"),
                      color: dataColors.orange,
                    },
                  ]}
                />
              </figure>
            </AnalyticsCard.Root>
          )}
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && hasProjectConnections && (
        <PageGrid.Column size="100%">
          <AnalyticsSection
            isFullWidth
            title={t("page.analytics-bookings.connections.section-title")}
            description={t("page.analytics-bookings.connections.description")}
          >
            <AnalyticsCard.Root variant="graph" height="content">
              <AnalyticsCard.Header
                title={t("page.analytics-bookings.connections.chart-title")}
                tooltip={help("page.analytics-bookings.connections.chart-title.tooltip")}
              />
              <figure className="flex min-h-scroll-area-compact flex-1 flex-col">
                <LineChart
                  data={audienceData}
                  xAxisType="date"
                  yLabel={t("page.analytics-bookings.axis.bookings")}
                  hoverLabel={(point) => (
                    <ul className="flex flex-col gap-space-xs">
                      {point.relatedPoints.map((related) => (
                        <li key={related.label}>
                          {related.label}: {related.dataPoint.y}
                        </li>
                      ))}
                      {point.relatedPoints.some((related) => related.dataPoint.isPending) && (
                        <li>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</li>
                      )}
                    </ul>
                  )}
                />
                <ChartLegend items={audienceData.map((series) => ({ label: series.label, color: series.color }))} />
              </figure>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}

      <PageGrid.Column size="100%">
        <AnalyticsSection
          title={t("page.analytics-bookings.patterns.section-title")}
          description={t("page.analytics-bookings.patterns.description")}
        >
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-bookings.patterns.weekday")}
              tooltip={help("page.analytics-bookings.patterns.weekday.tooltip")}
            />
            <HistogramChart
              data={weekdayData}
              onClickBar={
                mode === "all"
                  ? (point) =>
                      onChartFilter({
                        type: "weekday",
                        value: point.category,
                        label: point.label ?? point.category,
                      })
                  : undefined
              }
            />
          </AnalyticsCard.Root>
          <AnalyticsCard.Root height="content">
            <AnalyticsCard.Header
              title={t("page.analytics-bookings.patterns.time-of-day")}
              tooltip={help("page.analytics-bookings.patterns.time-of-day.tooltip")}
            />
            <HistogramChart
              data={timeOfDayData}
              onClickBar={
                mode === "all"
                  ? (point) =>
                      onChartFilter({
                        type: "timeOfDay",
                        value: point.category,
                        label: point.label ?? point.category,
                      })
                  : undefined
              }
            />
          </AnalyticsCard.Root>
          {mode === "all" && (
            <>
              <AnalyticsCard.Root height="content">
                <AnalyticsCard.Header
                  title={t("page.analytics-bookings.patterns.duration")}
                  tooltip={help("page.analytics-bookings.patterns.duration.tooltip")}
                />
                <HistogramChart
                  data={durationData}
                  onClickBar={(point) =>
                    onChartFilter({
                      type: "duration",
                      value: point.category,
                      label: point.label ?? point.category,
                    })
                  }
                />
              </AnalyticsCard.Root>
              <AnalyticsCard.Root height="content">
                <AnalyticsCard.Header
                  title={t("page.analytics-bookings.patterns.booker-mix")}
                  tooltip={help("page.analytics-bookings.patterns.booker-mix.tooltip")}
                />
                <HistogramChart data={bookerData} />
              </AnalyticsCard.Root>
            </>
          )}
        </AnalyticsSection>
      </PageGrid.Column>

      {mode === "all" && !isPortfolio && (
        <PageGrid.Column size="100%">
          <AnalyticsSection isFullWidth title={t("page.analytics-bookings.table.section-title")}>
            <AnalyticsCard.Root horizontalScroll>
              <AnalyticsCard.Header title={t("page.analytics-bookings.table.title")}>
                <div className="flex w-full flex-col gap-space-md @sm:w-auto @sm:flex-row @sm:items-center">
                  {chartFilter && selectedTableTab === "bookings" && (
                    <Inline gap="sm" align="center" wrap={false}>
                      <span className="text-caption text-text-secondary">
                        {t("page.analytics-bookings.table.chart-filter", { value: chartFilter.label })}
                      </span>
                      <Button variant="subtle" size="sm" onClick={onClearChartFilter}>
                        {t("common.action.clear")}
                      </Button>
                    </Inline>
                  )}
                  <SearchInput
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={
                      selectedTableTab === "bookings"
                        ? t("page.analytics-bookings.table.search")
                        : t("page.analytics-bookings.assets-table.search")
                    }
                    aria-label={
                      selectedTableTab === "bookings"
                        ? t("page.analytics-bookings.table.search")
                        : t("page.analytics-bookings.assets-table.search")
                    }
                    width="compact"
                  />
                  <Button variant="soft" icon={<FilterFunnel01Icon />} onClick={onOpenFilters}>
                    <Inline gap="sm" align="center" wrap={false}>
                      {t("common.action.filter")}
                      {activeFilterCount > 0 && <NumericBadge count={activeFilterCount} variant="filled" />}
                    </Inline>
                  </Button>
                </div>
              </AnalyticsCard.Header>
              <Tabs.Root<BookingAnalyticsTableTab>
                tabs={tableTabs}
                activeTab={selectedTableTab}
                onSelectTab={onTableTabChange}
              >
                <Tabs.Content value={selectedTableTab}>
                  {selectedTableTab === "bookings" ? (
                    <>
                      <figure className="invisible hidden @sm:visible @sm:block">
                        <Table table={bookingTable} data-testid="analytics-bookings-table">
                          {resultCount > 0 && (
                            <TableFooter
                              from={rangeFrom}
                              to={rangeTo}
                              total={resultCount}
                              page={page}
                              pageCount={pageCount}
                              onPageChange={onPageChange}
                            />
                          )}
                        </Table>
                      </figure>
                      <MobileTableCards table={bookingTable} dataTestId="analytics-bookings-mobile-list" />
                    </>
                  ) : (
                    <>
                      <figure className="invisible hidden @sm:visible @sm:block">
                        <Table
                          table={assetTable}
                          data-testid="analytics-bookable-assets-table"
                          getRowHref={(row) => routes.bookings.assetDetail({ slug: slug!, id: row.original.id })}
                        >
                          {resultCount > 0 && (
                            <TableFooter
                              from={rangeFrom}
                              to={rangeTo}
                              total={resultCount}
                              page={page}
                              pageCount={pageCount}
                              onPageChange={onPageChange}
                            />
                          )}
                        </Table>
                      </figure>
                      <MobileTableCards
                        table={assetTable}
                        dataTestId="analytics-bookable-assets-mobile-list"
                        getRowHref={(row) => routes.bookings.assetDetail({ slug: slug!, id: row.id })}
                      />
                    </>
                  )}
                  {resultCount > 0 && (
                    <footer className="mt-space-md @sm:hidden">
                      <TableFooter
                        from={rangeFrom}
                        to={rangeTo}
                        total={resultCount}
                        page={page}
                        pageCount={pageCount}
                        onPageChange={onPageChange}
                      />
                    </footer>
                  )}
                </Tabs.Content>
              </Tabs.Root>
            </AnalyticsCard.Root>
          </AnalyticsSection>
        </PageGrid.Column>
      )}
    </PageGrid.Row>
  );
}

function MobileTableCards<TData extends RowData>({
  table,
  dataTestId,
  getRowHref,
}: {
  table: DashboardTable<TData>;
  dataTestId: string;
  getRowHref?: (row: TData) => string;
}): React.ReactNode {
  return (
    <section className="flex flex-col gap-space-md @sm:hidden" data-testid={dataTestId}>
      {table.getRowModel().rows.map((row) => {
        const content = (
          <dl className="flex flex-col gap-space-md">
            {row.getVisibleCells().map((cell) => {
              const header = cell.column.columnDef.header;

              return (
                <Spread key={cell.id} space="md" align="start">
                  <dt className="text-caption text-text-secondary">
                    {typeof header === "string" ? header : cell.column.id}
                  </dt>
                  <dd className="min-w-0 text-right text-caption-emphasis">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </dd>
                </Spread>
              );
            })}
          </dl>
        );
        const href = getRowHref?.(row.original);

        return href ? (
          <Link key={row.id} className="text-text-primary no-underline" to={href}>
            <SectionCard>{content}</SectionCard>
          </Link>
        ) : (
          <SectionCard key={row.id}>{content}</SectionCard>
        );
      })}
    </section>
  );
}

function TableFooter({
  from,
  to,
  total,
  page,
  pageCount,
  onPageChange,
}: {
  from: number;
  to: number;
  total: number;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Inline gap="md" align="center" justify="between">
      <span className="text-caption text-text-secondary">
        {t("page.analytics-bookings.table.results", { from, to, total })}
      </span>
      {pageCount > 1 && <Pagination count={pageCount} currentIndex={page} onChange={onPageChange} />}
    </Inline>
  );
}

function formatAssetLabel(value: string): string {
  return value.length > 14 ? `${value.slice(0, 13)}…` : value;
}
